<template>
    <div class="left-side-menu left-side-menu-dark">
        <template v-if="showShopSwitcher">
            <ShopSwitcher />
            <hr class="mt-0">
        </template>
        <template v-if="showSpaceSwitcher">
            <SpaceSwitcher />
            <hr class="mt-0">
        </template>
        <div class="sidebar-content">
            <vue-perfect-scrollbar v-for="(menuItems, menuItemsIndex) in [topItems, bottomItems]" :key="menuItemsIndex" :settings="settings">
                <div class="sidebar-menu">
                    <ul>
                        <template v-for="(firstLevel, firstLevelKey) in menuItems">
                            <li
                                v-if="firstLevel.isActive !== false"
                                :key="JSON.stringify(firstLevel)"
                                class="side-nav-title side-nav-item"
                                :class="{ 'subnav nav-second-level': firstLevel.children, expanded: isExpanded(firstLevelKey), active: isActive(firstLevelKey) }"
                            >
                                <b-link v-bind="!firstLevel.children ? { to: firstLevel.to, href: firstLevel.href, target: firstLevel.target } : { href: '#' }" @click="firstLevelClick(firstLevel, firstLevelKey)">
                                    <img v-if="firstLevel.img" class="nav-link-icon" :src="getIconPath(firstLevel.img)" alt="">
                                    <feather v-else-if="firstLevel.icon" :type="firstLevel.icon" />
                                    <span class="link-label">{{ firstLevel.label }}</span>
                                    <feather v-if="firstLevel.children" class="menu-arrow" size="12" type="chevron-right" />
                                </b-link>
                                <template v-if="firstLevel.children">
                                    <b-collapse :visible="isExpanded(firstLevelKey)">
                                        <ul class="nav-second-level">
                                            <template v-for="secondLevel in firstLevel.children">
                                                <li v-if="secondLevel.isActive !== false" :key="JSON.stringify(secondLevel)">
                                                    <b-link :to="secondLevel.to" @click="secondLevelClick(secondLevel)">
                                                        {{ secondLevel.label }}
                                                    </b-link>
                                                </li>
                                            </template>
                                        </ul>
                                    </b-collapse>
                                </template>
                            </li>
                        </template>
                    </ul>
                </div>
            </vue-perfect-scrollbar>
        </div>
    </div>
</template>

<script>
    import VuePerfectScrollbar from 'vue-perfect-scrollbar';
    import ShopSwitcher from '@/components/common/ShopSwitcher.vue';
    import usercomService from '@/services/usercomService';
    import SpaceSwitcher from './common/SpaceSwitcher.vue';

    export default {
        name: 'AppSidebar',
        components: {
            VuePerfectScrollbar,
            ShopSwitcher,
            SpaceSwitcher,
        },
        props: {
            items: {
                type: Array,
            },
        },
        data() {
            return {
                settings: {
                    minScrollbarLength: 60,
                },
                expanded: { key: null, value: null },
                active: { key: null, value: null },
                collapsed: false,
            };
        },
        computed: {
            userRole() {
                return this.$store.getters['auth/getRoleName'];
            },
            showShopSwitcher() {
                return this.$store.getters['auth/isRoleAdvertiser'];
            },
            showSpaceSwitcher() {
                return this.$store.getters['auth/isRoleProducer'];
            },
            currentSpace() {
                return this.$store.getters['space/currentSpace'];
            },
            currentSpaceName() {
                return this.currentSpace?.name || '';
            },
            topItems() {
                return this.items.filter(item => !item.bottom);
            },
            bottomItems() {
                return this.items.filter(item => item.bottom);
            },
        },
        watch: {
            active() {
                if (Number.isFinite(this.active?.key) && this.items[this.active.key].children) {
                    this.expanded = { ...this.active };
                } else {
                    this.expanded = { key: null, value: null };
                }
            },
            '$route.path': {
                immediate: true,
                handler() {
                    this.initCurrentActive();
                },
            },
        },
        methods: {
            firstLevelClick(item, key) {
                if (item.children) {
                    this.expanded = { key, value: !this.expanded.value || this.expanded.key !== key };
                } else {
                    this.clickOnMenuItem(item);
                }
            },
            secondLevelClick(item, key) {
                this.clickOnMenuItem(item);
            },
            clickOnMenuItem(item) {
                usercomService.userengage(`event.menu-${item.label}`, { 'space-type': this.currentSpaceName });
            },
            initCurrentActive() {
                const currentActiveIndex = this.items.findIndex(item => {
                    if (item.children) return item.children.some(navItem => this.$route.matched.some(({ name }) => name === navItem?.to?.name));
                    return item.to?.name === this.$route.name;
                });
                this.active = currentActiveIndex !== -1 ? { key: currentActiveIndex, value: true } : { key: null, value: null };
            },
            isExpanded(key) {
                return key === this.expanded.key && this.expanded.value;
            },
            isActive(key) {
                return key === this.active.key && this.active.value;
            },
            getIconPath(name) {
                return require(`@/assets/images/menu-icons/${name}`); // eslint-disable-line
            },
        },
    };
</script>

<style lang="scss">
    .ps > .ps__scrollbar-y-rail {
        width: 8px !important;
        background-color: transparent !important;
    }
    .ps > .ps__scrollbar-y-rail > .ps__scrollbar-y,
    .ps.ps--in-scrolling.ps--y > .ps__scrollbar-y-rail > .ps__scrollbar-y,
    .ps > .ps__scrollbar-y-rail:active > .ps__scrollbar-y,
    .ps > .ps__scrollbar-y-rail:hover > .ps__scrollbar-y {
        width: 6px !important;
    }
    .abstract-items {
        display: none;
    }
    .btn-quick-start {
        margin-left: 30px;
    }

    .left-side-menu {
        .sidebar-content {
            .nav-link-icon {
                width: 24px;
                height: 24px;
            }
            display: flex;
            flex-direction: column;
            height: 100%;
            gap: 3rem;
            justify-content: space-between;

            .sidebar-menu > {
                ul {
                    margin: 0;
                    padding: 0;

                    li {
                        transition: all 0.3s ease;
                        list-style: none;

                        a {
                            transition: all 0.3s ease;

                            &:hover {
                                color: $menu-item-color-dark;
                            }

                            &.router-link-active {
                                color: $menu-item-active-color-dark;

                                &:hover {
                                    color: $menu-item-active-color-dark;
                                }
                            }
                        }

                        &.expanded {
                            background-color: lighten($bg-leftbar-dark, 2%);

                            &:not(.active) {
                                a {
                                    border-left-color: transparent;
                                    color: $menu-item-color-dark;

                                    svg {
                                        color: $menu-item-color-dark;
                                    }
                                }
                            }
                        }
                    }

                    > li {
                        > a {
                            &:hover {
                                color: $menu-item-hover-color-dark;
                                background-color: lighten($bg-leftbar-dark, 4%);
                                text-decoration: none;
                            }
                        }
                    }
                }
            }

            .nav-link-icon {
                width: 24px;
                height: 24px;
                margin: 0 14px 0 3px;
            }
        }
    }
</style>
